import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { ShoppingBasket, Trash2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import NotFound from '@/components/layout/not-found.tsx';
import cartStore from '@/stores/cart.store.ts';
import { getServiceById } from '@/lib/server-fns.ts';
import Section from '@/components/layout/section.tsx';
import PageTitle from '@/components/layout/page-title.tsx';
import Breadcrumbs from '@/components/layout/breadcrumbs.tsx';

const { VITE_LOCALE } = import.meta.env;

export const Route = createFileRoute('/services/$serviceId')({
	component: Service,
	loader: async ({ params }) => getServiceById({ data: { serviceId: params.serviceId } }),
	head: ({ loaderData }) => ({
		meta: [
			{
				name: 'apple-mobile-web-app-title',
				content: `Jervice - ${loaderData?.name || t('global.not_found.title')}`,
			},
			{
				name: 'og:title',
				content: `Jervice - ${loaderData?.name || t('global.not_found.title')}`,
			},
			{
				name: 'og:description',
				content: loaderData?.descriptionOrNull || t('global.not_found.description'),
			},
			{
				name: 'og:url',
				content: `https://jervice.${VITE_LOCALE === 'en' ? 'eu' : 'pl'}/services/${loaderData?.id}`,
			},
			{
				name: 'og:image',
				content: loaderData?.photoUrl || `/images/main.png`,
			},
			{
				name: 'twitter:title',
				content: `Jervice - ${loaderData?.name || t('global.not_found.title')}`,
			},
			{
				name: 'twitter:description',
				content: loaderData?.descriptionOrNull || t('global.not_found.description'),
			},
			{
				name: 'twitter:url',
				content: `https://jervice.${VITE_LOCALE === 'en' ? 'eu' : 'pl'}/services/${loaderData?.id}`,
			},
			{
				name: 'twitter:image',
				content: loaderData?.photoUrl || `/images/main.png`,
			},
			{ title: `Jervice - ${loaderData?.name || t('global.not_found.title')}` },
		],
	}),
});

function Service() {
	const { t: tr } = useTranslation();

	const service = useLoaderData({ from: '/services/$serviceId' });

	const { addItem, deleteItem } = cartStore(
		useShallow((state) => ({ addItem: state.addItem, deleteItem: state.deleteItem })),
	);

	const isEmpty = !service?.id;

	const { items } = cartStore(
		useShallow((state) => ({ addItem: state.addItem, items: state.items })),
	);

	const currencyFormatter = new Intl.NumberFormat(VITE_LOCALE, {
		style: 'currency',
		currency: VITE_LOCALE === 'pl' ? 'PLN' : 'PLN',
	});

	const handleAddItem = (subService: any) => {
		addItem({
			...subService,
			service: service?.name,
			serviceImage: service?.photoUrl,
			serviceId: service?.id,
		});
	};

	return isEmpty ? (
		<NotFound />
	) : (
		<Section className="flex flex-col py-6 max-md:py-4 gap-4 max-sm:gap-2">
			<Breadcrumbs
				items={[
					{ title: t('global.menu.home'), link: '/' },
					{ title: t('services.all_services'), link: '/services' },
					{ title: service?.name },
				]}
			/>

			<div className="flex gap-6 items-start max-lg:flex-wrap mt-4">
				<div className="flex flex-col flex-1 max-lg:flex-auto order-1 max-lg:p-0">
					<PageTitle className="text-[var(--primary)] mb-2">{service?.name}</PageTitle>

					<p className="text-lg max-xl:text-[16px] text-neutral-500">
						{service?.descriptionOrNull}
					</p>
				</div>

				<div className="w-full aspect-video flex-1 max-lg:flex-auto relative order-2 max-lg:p-0">
					<img
						src={service?.photoUrl || ''}
						alt={service?.name}
						className="object-cover w-full h-full rounded-xl"
						style={{
							viewTransitionName: `service-image-${service.id}`,
						}}
					/>
				</div>
			</div>

			<div className="w-full h-1 bg-neutral-100" />

			{service?.subServices?.length ? (
				<div className="grid grid-cols-12 gap-4">
					{service?.subServices?.map((subService: any, i: number) => {
						const isAdded = items?.find((item) => item?.id === subService?.id);

						return (
							<div
								className="flex flex-col gap-6 col-span-4 max-2xl:col-span-4 max-xl:col-span-6 max-lg:col-span-12 p-4 bg-[var(--light)] rounded-xl"
								key={`sub_service_${subService?.id}_${i}`}
							>
								<h2 className="font-bold text-4xl max-xl:text-3xl max-lg:text-2xl text-neutral-600">
									{subService?.name}
								</h2>

								<p className="text-lg max-xl:text-[16px] max-xl:leading-7 text-neutral-600 leading-8">
									{subService?.descriptionOrNull}
								</p>

								<div className="flex flex-col items-start gap-3 mt-auto">
									<div className="bg-white rounded-xl py-2 px-2 font-goldman font-bold w-fit">
										{tr('services.sub_service_start_from', {
											qty: currencyFormatter.format(subService?.price),
										})}{' '}
										{subService?.currency}
									</div>

									<button
										className="bg-black rounded-xl flex items-center gap-2 w-fit px-4 py-2  text-white cursor-pointer hover:bg-black/80 goldman data-[added=true]:bg-red-500 data-[added=true]:hover:bg-red-600 font-medium transition-all active:scale-97"
										data-added={!!isAdded}
										onClick={() => {
											if (isAdded) {
												deleteItem(subService?.id);
											} else {
												handleAddItem(subService);
											}
										}}
									>
										{tr(isAdded ? 'global.in_cart' : 'global.add_to_cart')}{' '}
										{isAdded ? <Trash2 size="1.25rem" /> : <ShoppingBasket size="1.25rem" />}
									</button>
								</div>
							</div>
						);
					})}
				</div>
			) : (
				<div className="flex flex-col items-center justify-center min-h-50">
					<p className="text-lg text-neutral-500">{tr('services.empty_subservices')}</p>
				</div>
			)}
		</Section>
	);
}
